import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { groups } from '../api/client';
import { BenchmarkBarChart, BenchmarkPieChart } from '../components/charts/BenchmarkDistribution';

const BENCHMARK_COLORS = {
  exceeding: '#10B981',
  meeting: '#3B82F6',
  approaching: '#F59E0B',
  below: '#EF4444',
};

const BENCHMARK_LABELS = {
  exceeding: 'Exceeding',
  meeting: 'Meeting',
  approaching: 'Approaching',
  below: 'Below',
};

export default function GroupAnalyticsPage() {
  const { groupId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [groupId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await groups.analytics(groupId);
      setData(response.data);
      // Select first assessment by default
      if (response.data.assessments.length > 0) {
        setSelectedAssessment(response.data.assessments[0]);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load group analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const { group_name, student_count, assessments, common_gaps, student_performance } = data;

  // Calculate overall benchmark distribution across all assessments
  const overallBenchmark = assessments.reduce(
    (acc, assessment) => {
      acc.exceeding += assessment.benchmark_distribution.exceeding;
      acc.meeting += assessment.benchmark_distribution.meeting;
      acc.approaching += assessment.benchmark_distribution.approaching;
      acc.below += assessment.benchmark_distribution.below;
      return acc;
    },
    { exceeding: 0, meeting: 0, approaching: 0, below: 0 }
  );

  const totalCompleted = Object.values(overallBenchmark).reduce((a, b) => a + b, 0);

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-4">
        <Link
          to={`/app/groups/${groupId}`}
          className="text-sm text-indigo-600 hover:text-indigo-500"
        >
          &larr; Back to {group_name}
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Group Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">
          {group_name} | {student_count} students
        </p>
      </div>

      {/* No data message */}
      {assessments.length === 0 ? (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No assessment data yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Complete assessments for students in this group to see analytics.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white shadow rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Total Students</p>
              <p className="mt-1 text-3xl font-semibold text-gray-900">{student_count}</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Assessments Completed</p>
              <p className="mt-1 text-3xl font-semibold text-gray-900">{totalCompleted}</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Meeting/Exceeding</p>
              <p className="mt-1 text-3xl font-semibold text-green-600">
                {totalCompleted > 0
                  ? Math.round(((overallBenchmark.exceeding + overallBenchmark.meeting) / totalCompleted) * 100)
                  : 0}%
              </p>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Needing Support</p>
              <p className="mt-1 text-3xl font-semibold text-amber-600">
                {totalCompleted > 0
                  ? Math.round(((overallBenchmark.approaching + overallBenchmark.below) / totalCompleted) * 100)
                  : 0}%
              </p>
            </div>
          </div>

          {/* Assessment Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Assessment Selector & Chart */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Benchmark Distribution</h2>
                {assessments.length > 1 && (
                  <select
                    value={selectedAssessment?.template_id || ''}
                    onChange={(e) => {
                      const assessment = assessments.find(a => a.template_id === parseInt(e.target.value));
                      setSelectedAssessment(assessment);
                    }}
                    className="text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {assessments.map(a => (
                      <option key={a.template_id} value={a.template_id}>{a.name}</option>
                    ))}
                  </select>
                )}
              </div>
              {selectedAssessment && (
                <>
                  <div className="mb-4 text-center">
                    <p className="text-sm text-gray-500">
                      {selectedAssessment.completed_count} completed | Avg: {selectedAssessment.avg_percentage}%
                    </p>
                  </div>
                  <BenchmarkPieChart data={selectedAssessment.benchmark_distribution} height={280} />
                </>
              )}
            </div>

            {/* Overall Distribution Bar Chart */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Overall Performance</h2>
              <BenchmarkBarChart data={overallBenchmark} height={280} />
            </div>
          </div>

          {/* Assessment Summary Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Assessment Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assessment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distribution
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.map((assessment) => (
                    <tr key={assessment.template_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {assessment.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {assessment.completed_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {assessment.avg_percentage}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          {Object.entries(assessment.benchmark_distribution).map(([level, count]) => (
                            count > 0 && (
                              <span
                                key={level}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: `${BENCHMARK_COLORS[level]}20`,
                                  color: BENCHMARK_COLORS[level]
                                }}
                              >
                                {count}
                              </span>
                            )
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Common Learning Gaps */}
          {common_gaps.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Common Learning Gaps
                <span className="ml-2 text-sm font-normal text-gray-500">
                  (Skills where multiple students struggle)
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {common_gaps.map((gap) => (
                  <div
                    key={gap.skill_code}
                    className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {gap.skill_name}
                      </p>
                      <p className="text-xs text-gray-500">{gap.skill_code}</p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {gap.struggling_count} students
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Student Performance Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Student Performance</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Latest Assessment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Latest Score
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {student_performance.map((student) => (
                    <tr key={student.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/app/students/${student.id}`}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                        >
                          {student.full_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.grade_level || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.assessments_completed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.latest_assessment ? (
                          <>
                            {student.latest_assessment}
                            <span className="text-xs text-gray-400 ml-1">
                              ({new Date(student.latest_date).toLocaleDateString()})
                            </span>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.latest_percentage !== null ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {Math.round(student.latest_percentage)}%
                            </span>
                            {student.latest_benchmark && (
                              <span
                                className="px-2 py-0.5 text-xs font-medium rounded-full"
                                style={{
                                  backgroundColor: `${BENCHMARK_COLORS[student.latest_benchmark]}20`,
                                  color: BENCHMARK_COLORS[student.latest_benchmark]
                                }}
                              >
                                {BENCHMARK_LABELS[student.latest_benchmark]}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No assessments</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <Link
                          to={`/app/students/${student.id}/progress`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View Progress
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
